import { ApolloClient, InMemoryCache, ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client";

import { APP_NAME, APP_VERSION, GetActiveAPIUrl } from "../helpers/config";
// eslint-disable-next-line import/no-cycle
import { GetLocalAuthToken, HandleExpiredToken } from "./MoblyzeAuth";

const httpLink = createUploadLink({
  uri: GetActiveAPIUrl(),
});

const authLink = setContext(async (_, { headers }) => {
  const token = await GetLocalAuthToken();

  return {
    headers: {
      ...headers,
      appID: "moblyze-web",
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

export function handleApolloError({ graphQLErrors, networkError }) {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (
        err.extensions?.code === "UNAUTHENTICATED" ||
        err.message?.toLowerCase().includes("jwt expired") ||
        err.message?.toLowerCase().includes("invalid token")
      ) {
        HandleExpiredToken();
        return;
      }
    }
  }
  if (
    networkError &&
    (networkError.statusCode === 401 || networkError.statusCode === 403)
  ) {
    HandleExpiredToken();
  }
}

const errorLink = onError(handleApolloError);

// Shared cache config. `Candidate` is keyed by candidateProfileId, not `id`.
// The triage queries select `candidateProfileId` (the canonical key) and no
// `id`, so without this policy Apollo can't normalize `Candidate` and every
// poll produces brand-new candidate objects — forcing every triage row and the
// chat drawer to re-render. Keying on candidateProfileId restores stable
// references for unchanged candidates across polls.
export const cacheConfig = {
  typePolicies: {
    Candidate: { keyFields: ["candidateProfileId"] },
  },
};

// Factory for building an ApolloClient with our standard links + cache config.
// The module-level singleton below uses it, and tests can use it to spin up
// throwaway clients (each with its own fresh cache) instead of writing into
// the shared singleton's cache.
export function createMoblyzeApolloClient(overrides = {}) {
  return new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, httpLink]),
    name: APP_NAME,
    version: APP_VERSION,
    cache: new InMemoryCache(cacheConfig),
    fetchPolicy: "network-only",
    ...overrides,
  });
}

const MoblyzeApolloClient = createMoblyzeApolloClient();

export default MoblyzeApolloClient;
